
/**
 * Prüft, ob der Browser das HTMLDialogElement unterstützt, also diese Dialogboxen hier nutzen kann.
 * Könnte z.B. in Templates verwendet werden, die derzeit immer einen bestimmten Key an den Server submitten, der dann immer (ohne weitere Vorabprüfungen/Fallunterscheidungen)
 * direkt eine ConfirmationBox ausliefert, über deren Submitbutton (z.B. mit demselben Key mit Suffix "confirmed") dann die eigentliche Aktion triggern.
 * In solchen Fällen könnte ein JS mit dieser Methode prüfen, ob alternativ auch ein JS-ConfirmationDialog unterstützt wird, und wenn ja, könnte
 * der Submitbutton in einen Button konvertiert und ein Eventhandler angebunden werden, der die ConfirmationBox direkt als Dialog anzeigt und bei Bestätigung
 * dann einen Request mit dem Confirmed-Key sendet.
 * (Andererseits wäre da vielleicht besser, direkt einen Submitbutton mit Confirmed-Key in den Dialog einzubauen, was die Methoden unten bislang noch nicht
 * unterstützen, die sind vielmehr auf eine Aync-Await-Anzeige mit anschließendem "Ok-gedrückt-Flag" und JS-Reaktion auf den OK-Button ausgelegt.
 * Aber das kann ja noch ergänzt werden.)
 * @returns {boolean}
 */
export function supportsDialog() {
    return 'HTMLDialogElement' in window;
}

export function supportsPopover() {
    return 'popover' in window.HTMLElement.prototype;
}

/*
 * Promise-Resolver für Confirmation-Dialoge. 
 * Bei einem wiederholten Anzeigen eines Confirmation-Dialogs, der schon vom ersten Aufruf her im DOM vorhanden ist und einfach nur erneut angezeigt
 * werden soll, wird zwar für die Neu-Anzeige ein neues Promise mit neuem Resolver erstellt, aber die Dialogbox mit ihrem Close-Eventhandler ist eben noch
 * dieselbe. Daher soll nur die Referenz auf den Resolver, der vom Eventhandler aufgerufen wird, aktualisiert werden und der Dialog wieder angezeigt werden
 * können. Genau dazu dient diese Variable: In der Promise-Erzeugung wird der Resolver hier gesichert und dann eben die Dialogbox angezeigt, egal ob sie
 * gerade erst eingefügt oder wieder neu geöffnet wurde. Das geht natürlich nur, wenn nicht mehrere solcher Dialogboxen gleichzeitig geöffnet werden können,
 * was hier durch die Modal-Anzeige aber gesichert sein sollte.
 */
let dialogResolver = null;

function createPlainDialog(decent, id, optionalTitle, contentHTML, cancelLabel, okLabel) {
    //Notizen zum Autofocus und VoiceOver:
    //- Firefox unterstützt das Fokussieren eines Dialogs, andere Browser nicht. Wenn ich das Autofocus-Attribut zum Dialog selbst hinzufüge,
    //  wird dieser in Firefox fokussiert und vom ScreenReader vorgelesen. Safari und Co fokussieren dann mangels Fokussierbarkeit des Dialogs
    //  den ersten Button, was hier der OK-Button is.
    //- Wenn Firefox den Dialog selbst fokussiert, sorgt mein Script dafür, dass Enter den OK-Button auslöst. In den anderen Browsern ist der OK-Button
    //  fokussiert und wird ebenfalls über Enter ausgelöst.
    //- Natürlich könnte man darüber nachdenken, statt dessen das Autofocus direkt an den OK-Button zu hängen statt an den Dialog.
    //  Der Grund, warum ich das _nicht_ gemacht habe:
    //- Während VoiceOver in Safari beim Autofokussieren des Buttons wenigstens das erste Element des Dialogs (hier h1) vorliest
    //  ("Dialogtitle und x andere Objekte"), funktioniert das in Firefox bei Autofokussierung des Buttons nicht: Dort wird dann nur der Button ohne Kontext
    //  vorgelesen. Daher ziehe ich es vor, dass in Firefox der Dialog autofokussiert wird.
    
    
    const d = document.createElement("dialog");
    if (id)
        d.id = id;
    if (decent)
        d.className = "decent";
    const f = document.createElement("form");
    f.setAttribute("method", "dialog");
    f.setAttribute("tabindex", "0");
    f.setAttribute("autofocus", "autofocus");
    f.innerHTML = contentHTML;
    f.insertAdjacentHTML("beforeend", "<div class='buttons'><button value='ok' class='default'>" + okLabel + "</button><button value='cancel'>" + cancelLabel + "</button></div>");
    if (typeof optionalTitle === "string") {
        const h = document.createElement("h1");
        h.innerText = optionalTitle;
        f.insertAdjacentElement("afterbegin", h);
    }
    d.appendChild(f);
    addConfirmationDialogKeypressListener(f, d);
    addConfirmationDialogKeypressListener(d, d);
    return d;
}

function _createConfirmationBoxDialog(id, title, contentHTML, method, action, okButtonHTML, cancelButtonHTML) {
    const d = document.createElement("dialog");
    d.setAttribute("class", "text-block");
    if (id)
        d.id = id;
    const titleId = id ? id + "_h" : "de_feu_oues_tempdialog_h";
    d.setAttribute("aria-labelled-by", titleId);
    const tb = document.createElement("div");
    tb.setAttribute("class", "text-block48 yellow");
    const f = document.createElement("form");
    f.setAttribute("method", method);
    f.setAttribute("tabindex", "0");
    f.setAttribute("autofocus", "autofocus");
    if (action !== null)
        f.setAttribute("action", action);
    f.innerHTML = contentHTML;
    f.insertAdjacentHTML("beforeend", "<div class='buttons'>" + okButtonHTML + cancelButtonHTML + "</div>");
    tb.appendChild(f);
    tb.insertAdjacentHTML("afterbegin", '<img src="/images/flat/alpha/confirm.svg" alt="Bestätigungsdialog"><h1 id="' + titleId + '">' + title + "</h1>");
    d.appendChild(tb);
    addConfirmationDialogKeypressListener(f, d);
    addConfirmationDialogKeypressListener(d, d);
    return d;
}

function createConfirmationBoxDialog(id, title, contentHTML, cancelLabel, okLabel) {
    const ok = typeof okLabel === "string" ? okLabel : "OK";
    const cancel = typeof cancelLabel === "string" ? cancelLabel : "Abbrechen";
    return _createConfirmationBoxDialog(id, title, contentHTML, "dialog", null, 
            '<button value="ok" class="default">' + ok + "</button>", 
            '<button value="cancel">' + cancel + "</button>");
}

function createConfirmationBoxSubmitDialog(id, title, contentHTML, cancelLabel, okLabel, okName, okValue, method, action) {
    const ok = typeof okLabel === "string" ? okLabel : "OK";
    const cancel = typeof cancelLabel === "string" ? cancelLabel : "Abbrechen";
    return _createConfirmationBoxDialog(id, title, contentHTML, method, action, 
            '<button type="submit" name="' + okName + '" value="' + okValue + '" class="default">' + ok + '</button>',
            '<button type="submit" formmethod="dialog">' + cancel + '</button>');
}

function addCloseOnEnterListener(target, dialog) {
    target.addEventListener("keypress", function(ev) {
        if (ev.target === target && /^(Numpad)?Enter$/i.test(ev.code)) {
            dialog.close();
        }
    });
}

function addConfirmationDialogKeypressListener(target, dialog) {
    target.addEventListener("keypress", function(ev) {
        //Enter soll Default-Button fokussieren
        if (ev.target === target && /^(Numpad)?Enter$/i.test(ev.code)) {
            const ok = dialog.querySelector("button.default");
            if (ok !== null)
                ok.focus();
            //Ursprünglich stand da mal ok.click().
            //Nun wird der OK-Button nur noch fokussiert. Es muss also ggf. zweimal Enter gedrückt werden. Der erste Druck hebt den Default-/OK-/Confirm-Button
            //nur hervor, der zweite Druck betätigt ihn dann.
            //Hauptgrund dafür war, dass man bei Screenreader-Nutzung so in jedem Fall auch nochmal das Label des Buttons vorgelesen bekommt und explizit
            //confirmen muss. Aber auch bei Bildschirmnutzung sollte das vertretbar sein und einem versehentlichen Bestätigen etwas zuvorkommen.
        } else if (/^[a-z]$/i.test(ev.key)) { 
            const k = ev.key.toLowerCase();
            //Wenn eine einfache Buchstabentaste gedrückt wurde...
            //und genau ein Button-Label mit diesem Buchstaben beginnt, sollen die Anfangsbuchstaben auch als Shortcut akzeptiert werden:
            const buttons = dialog.querySelectorAll(".buttons button");
            let lastHit = null;
            let duplicates = false;
            for (const b of buttons) {
                if (b.textContent.toLowerCase().charAt(0) === k) {
                    if (lastHit !== null)
                        duplicates = true;
                    lastHit = b;
                }
            }
            if (lastHit !== null && !duplicates) 
                lastHit.click();
        }
    });
}

function _createMessageBoxDialog(id, title, contentHTML, okLabel, color, icon, iconAlt) {
    const d = document.createElement("dialog");
    d.setAttribute("class", "text-block");
    if (id)
        d.id = id;
    const titleId = id ? id + "_h" : "de_feu_oues_tempdialog_h";
    d.setAttribute("aria-labelled-by", titleId);
    const tb = document.createElement("div");
    tb.setAttribute("class", "text-block48 " + color);
    const f = document.createElement("form");
    f.setAttribute("method", "dialog");
    f.setAttribute("tabindex", "0");
    f.setAttribute("autofocus", "autofocus");
    f.innerHTML = contentHTML;
    const ok = typeof okLabel === "string" ? okLabel : "OK";
    f.insertAdjacentHTML("beforeend", '<div class="buttons"><button class="default">' + ok + '</button></div>');
    tb.appendChild(f);
    tb.insertAdjacentHTML("afterbegin", '<img src="/images/flat/alpha/' + icon + '.svg" alt="' + iconAlt + '"><h1 id="' + titleId + '">' + title + "</h1>");
    d.appendChild(tb);
    addCloseOnEnterListener(f, d);
    addCloseOnEnterListener(d, d);
    return d;
}

function createErrorBoxDialog(id, title, contentHTML, okLabel) {
    return _createMessageBoxDialog(id, title, contentHTML, okLabel, "red", "error", "Fehler");
}

function createWarningBoxDialog(id, title, contentHTML, okLabel) {
    return _createMessageBoxDialog(id, title, contentHTML, okLabel, "yellow", "warning", "Achtung");
}

function createInfoBoxDialog(id, title, contentHTML, okLabel) {
    return _createMessageBoxDialog(id, title, contentHTML, okLabel, "blue", "info", "Info");
}

function insertConfirmationDialog(d, removeOnClose) {
    document.body.appendChild(d);
    
    d.addEventListener("close", function() {
        if (d.returnValue === "ok") {
            if (removeOnClose)
                d.remove();
            else
                d.returnValue = ""; //Damit bei wiederholtem Dialogaufruf eine Esc-Taste nicht für "ok" gehalten wird (denn bei Esc wird returnValue unverändert gelassen).
                    //Esc löst übrigens sowohl das Event "cancel" als auch "close" aus. Aber mit dieser Konstruktion muss "cancel"-Event nicht extra abgefangen werden.
            if (typeof dialogResolver === "function")
                dialogResolver(true);
        } else {
            if (removeOnClose)
                d.remove();
            if (typeof dialogResolver === "function")
                dialogResolver(false);
        }
    });
    return d;
}

function insertMessageDialog(d, removeOnClose) {
    document.body.appendChild(d);
    if (removeOnClose)
        d.addEventListener("close", () => d.remove());
    return d;
}

/**
 * Anzeigen eines "neutralen" Bestätigungsdialogs (nicht im gelben ConfirmationBox-Design).
 * Nutzung per await confirmationDialog(...) mit Rückgabe eines Booleans (true für OK, false für Cancel).
 * @param {String} optionalTitle Wenn typeof String, dann wird dieser als Überschrift h1 in den Dialog eingefügt, andernfalls wird eine Dialogbox ohne Überschrift erzeugt.
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne die Überschrift und die Buttons)
 * @param {String} cancelLabel Label für den Cancel-Button (Default "Abbrechen")
 * @param {String} okLabel Label für den OK-Button (Default "OK")
 * @param {String} fallbackContentText Plaintext-Version von contentHTML, die in einer Standard-Dialogbox (confirm(...)) angezeigt wird, falls !supportsDialog().
 * Wenn supportsDialog vorher geprüft wurde, kann dieses Argument auch weggelassen werden.
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise für die Rückgabe eines Booleans, sobald der Dialog geschlossen wurde (true für OK, false für Cancel)
 */
export async function confirmationDialog(optionalTitle, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId) {
    return await _confirmationDialog(false, optionalTitle, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId);
}

/**
 * Genau wie confirmationDialog, aber mit dezenterer Animation beim Einblenden
 * @param {String} optionalTitle Wenn typeof String, dann wird dieser als Überschrift h1 in den Dialog eingefügt, andernfalls wird eine Dialogbox ohne Überschrift erzeugt.
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne die Überschrift und die Buttons)
 * @param {String} cancelLabel Label für den Cancel-Button (Default "Abbrechen")
 * @param {String} okLabel Label für den OK-Button (Default "OK")
 * @param {String} fallbackContentText Plaintext-Version von contentHTML, die in einer Standard-Dialogbox (confirm(...)) angezeigt wird, falls !supportsDialog().
 * Wenn supportsDialog vorher geprüft wurde, kann dieses Argument auch weggelassen werden.
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise für die Rückgabe eines Booleans, sobald der Dialog geschlossen wurde (true für OK, false für Cancel)
 */
export async function decentConfirmationDialog(optionalTitle, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId) {
    return await _confirmationDialog(true, optionalTitle, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId);
}

async function _confirmationDialog(decent, optionalTitle, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId) {
        if (!supportsDialog())
        return new Promise(resolve => {
            const r = confirm(fallbackContentText);
            resolve(r);
        });
    else {
        return new Promise(resolve => {
            dialogResolver = resolve;
            let d = optionalId ? document.getElementById(optionalId) : null;
            if (d === null)
                d = insertConfirmationDialog(createPlainDialog(decent, optionalId, optionalTitle, contentHTML, cancelLabel, okLabel), !optionalId);
            d.showModal();
        });
    }
}

/**
 * Anzeigen einer Bestätigungsdialogs im ConfirmationBox-Design (gelb, mit Warnungs-Symbol).
 * Nutzung per await confirmationDialog(...) mit Rückgabe eines Booleans (true für OK, false für Cancel).
 * <p>
 * Wenn die Nutzung in einem Eventhandler wie insb. onSubmit erfolgen soll, ist zu beachten, dass natürlich die Entscheidung, ob das Event abgearbeitet
 * werden darf oder mit preventDefault() abgebrochen werden soll, sofort synchron erfolgen muss! Anders als die klassische window.confirm()-Method
 * kann diese Methode mit await dort also nicht einfach so eingesetzt werden wie "if (await confirmationBoxDialog() ... { submit} else { ev.preventDefault();}"!
 * <br>
 * In diesem Fall ist das Event in jedem Fall vor dem ansynchronen Aufruf abzubrechen, und nach erfolgter Confirmation soll es neu augelöst werden, wobei
 * dann die erneute Anzeige der ConfirmationBox zu unterdrücken ist (z.B. durch Abmelden eines Eventhandlers), also bspw. in folgender Art:
 * <pre><code> 
 * async function preSubmitListener(ev) {
 *     ev.preventDefault(); //Diesen Submit erstmal abbrechen. (Ein Await würde ihn nicht pausieren!) Wenn OK gedrückt wird, dann wird erneut ein Submit getriggert.
 *     if (await confirmationBoxDialog(...) { // jQuery | Vanilla
 *         $form.off("submit", preSubmitListener); //   | removeEventListener("submit", preSubmitListener);
 *         $form.trigger("submit");                //   | form.requestSubmit();  oder form.requestSubmit(specificSubmitButton);
 *     }
 * }</code></pre>
 * Natürlich geht das nur, wenn nicht derselbe Eventhandler noch andere Dinge macht, die er auch oder gerade im Fall nach der Confirmation erledigen soll
 * und daher nicht abgemeldet werden darf. Dann kann man stattdessen z.B. irgendwo ein Flag speichern, dass eine Confirmation vorliegt.
 * Das Ganze kann aber noch Probleme machen in Browsern ohne Dialog-Support, weil das requestSubmit() wiederum anscheinend nur nach einem Async-Dialog
 * funktioniert, aber nicht im selben Eventhandler, der den Submit gerade abgefangen hat. (Testbar per Debugger, indem ein Breakpoint auf die await ConfirmationBox-Zeile
 * gesetzt wird und dort vor Ausführung "delete window.HTMLDialogElement" ausgeführt wird.)
 * Dann sollte im Fall !supportsDialog() besser das Request-Submit in nach einem Timeout ausgeführt werden.
 *
 * @param {String} title Titel für die Überschrift der ConfirmationBox. Hier (i.Ggs. zu confirmationDialog()) ein Pflichtparameter!
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne die Überschrift und die Buttons)
 * @param {String} cancelLabel Label für den Cancel-Button (Default "Abbrechen")
 * @param {String} okLabel Label für den OK-Button (Default "OK")
 * @param {String} fallbackContentText Plaintext-Version von contentHTML, die in einer Standard-Dialogbox (confirm(...)) angezeigt wird, falls !supportsDialog().
 * Wenn supportsDialog vorher geprüft wurde, kann dieses Argument auch weggelassen werden.
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise für die Rückgabe eines Booleans, sobald der Dialog geschlossen wurde (true für OK, false für Cancel)
 */
export async function confirmationBoxDialog(title, contentHTML, cancelLabel, okLabel, fallbackContentText, optionalId) {
    if (!supportsDialog())
        return new Promise(resolve => {
            const r = confirm(fallbackContentText);
            resolve(r);
        });
    else {
        return new Promise(resolve => {
            dialogResolver = resolve;
            let d = optionalId ? document.getElementById(optionalId) : null;
            if (d === null) 
                d = insertConfirmationDialog(createConfirmationBoxDialog(optionalId, title, contentHTML, cancelLabel, okLabel), !optionalId);
            d.showModal();
        });
    }
}

/**
 * Zeigt eine ConfirmationBox als modalen Dialog an, wartet aber nicht auf die Eingabe. Vielmehr soll der OK-Button des Dialogs ein Submit-Button
 * sein, der einen Post- oder Get-Request auslöst, während der Cancel-Button den Dialog wieder schließt. Ein Mechanismus zum Abwarten, ob der Dialog
 * wieder geschlossen/abgebrochen wurde, ist derzeit nicht vorgesehen (ggf. später nachrüstbar).
 * Insofern gar nicht mit den Async-Functions wie confirmationBoxDialog vergleichbar, die in jedem Fall nach Schließen des Dialogs eine Rückmeldung
 * geben, ob er abgebrochen oder mit OK bestätigt wurde und für beide Events (zumindest OK, ggf. aber eben auch Cancel) eine Reaktion ermöglichen.
 * 
 * Im Gegensatz zu confirmationBoxDialog und Co erfolgt _hier_ auch keine Browserweiche. Es wird vielmehr <em>vorausgesetzt</em>, dass der Browser
 * Dialoge unterstützt. In Einsatzszenarien, in denen Abwärtskompatibilität zu älteren Browsern gefragt ist, <em>muss der Aufrufer vorher mittels
 * {@function supportsDialog()} überprüfen, ob diese Methode aufrufbar ist.
 * Ein typisches Anwendungsszenario wäre: Im Formular ist ein Submit-Button enthalten, der im Fallback-Fall (wenn JS ganz abgeschaltet ist oder !supportsDialog())
 * einen Submit auslöst, auf welchen hin serverseitig eine Prüfung vorgenommen und ggf. eine Response mit hart eingebetteter ConfirmationBox mit Submitbutton
 * ausliefert. Wenn aber JS aktiv ist und supportsDialog() true antwortet, wird dort ein Eventhandler angebunden, der den Submit unterbindet und statt dessen
 * selbst diesen ConfirmationBoxSubmitDialog anzeigt. Der Text muss dazu entweder redundant im JS (für diesen Dialog) und Java (fürs Fallback) stehen, oder
 * der Content könnte auch per Ajax-Call vom Server abgerufen werden. Auch könnte so per Ajax-Call überprüft werden, ob überhaupt eine Confirmation nötig ist
 * oder direkt ein Submit ausgelöst werden soll oder eine Fehlerbox angezeigt werden soll.
 * @param {String} title Titel für die Überschrift der ConfirmationBox. Hier (i.Ggs. zu confirmationDialog()) ein Pflichtparameter!
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne die Überschrift und die Buttons)
 * @param {String} cancelLabel Label für den Cancel-Button (Default "Abbrechen")
 * @param {String} okLabel Label für den OK-Button (Default "OK")
 * @param {String} okName Name-Attribut des OK-Buttons (Key des zu submittenden Werts)
 * @param {String} okValue Value-Attribut des OK-Buttons (zu submittender Wert)
 * @param {String} method Form-Method-Attribut: post oder get
 * @param {String} action Form-Action-Attribut: URL für Submit
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Das sollte bei dieser konkreten Methode wohl der Default sein.
 * Wenn falsy, wird bei Close-Events (also keinem Submit) der Dialog ohne ID wieder aus dem HTML entfernt und bei wiederholtem Aufruf ein neuer erzeugt/injiziert.
 * @returns {undefined} void
 */
export function showConfirmationBoxSubmitDialog(title, contentHTML, cancelLabel, okLabel, okName, okValue, method, action, optionalId) {
    let d = optionalId ? document.getElementById(optionalId) : null;
    if (d === null) {
        d = insertConfirmationDialog(createConfirmationBoxSubmitDialog(optionalId, title, contentHTML, cancelLabel, okLabel, okName, okValue, method, action), !optionalId);
    }
    d.showModal();
}

/**
 * Synchrone Funktion zum reinen Anzeigen eines Fehlerboxdialogs.
 * @param {String} title Titel für die Überschrift der ErrorBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog().
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Element} Dialog-Element
 */
export function showErrorBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    if (!supportsDialog()) 
        alert(fallbackContentText);
    else {
        let d = optionalId ? document.getElementById(optionalId) : null;
        if (d === null)
            d = insertMessageDialog(createErrorBoxDialog(optionalId, title, contentHTML, okLabel), !optionalId);
        d.showModal();
        return d;
    }
}

/**
 * Synchrone Funktion zum reinen Anzeigen eines Warnungsboxdialogs.
 * @param {String} title Titel für die Überschrift der WarningBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog().
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Element} Dialog-Element
 */
export function showWarningBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    if (!supportsDialog()) 
        alert(fallbackContentText);
    else {
        let d = optionalId ? document.getElementById(optionalId) : null;
        if (d === null)
            d = insertMessageDialog(createWarningBoxDialog(optionalId, title, contentHTML, okLabel), !optionalId);
        d.showModal();
        return d;
    }
}

/**
 * Synchrone Funktion zum reinen Anzeigen eines Infoboxdialogs.
 * @param {String} title Titel für die Überschrift der InfoBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog().
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Element} Dialog-Element
 */
export function showInfoBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    if (!supportsDialog()) 
        alert(fallbackContentText);
    else {
        let d = optionalId ? document.getElementById(optionalId) : null;
        if (d === null)
            d = insertMessageDialog(createInfoBoxDialog(optionalId, title, contentHTML, okLabel), !optionalId);
        d.showModal();
        return d;
    }
}

/**
 * Asynchrone Version von showErrorBoxDialog: Diese Funktion kann mit await aufgerufen werden, woraufhin der Aufrufer wartet, bis die Dialogbox
 * wieder geschlossen wurde.
 * @param {String} title Titel für die Überschrift der ErrorBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog().
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise resolved immer zu true, nachdem die Box geschlossen wurde (egal ob per OK-Button oder Esc-Taste)
 */
export async function errorBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    return new Promise(resolve => {
        let d = showErrorBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId);
        d.addEventListener("close", () => resolve(true));
    });
}

/**
 * Asynchrone Version von showWarningBoxDialog: Diese Funktion kann mit await aufgerufen werden, woraufhin der Aufrufer wartet, bis die Dialogbox
 * wieder geschlossen wurde.
 * @param {String} title Titel für die Überschrift der ErrorBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog(). Kann beliebig (auch undefined) bleiben, 
 * falls supportsDialog() sichergestellt ist (durch Aufruf, oder  implizit, wenn die Meldung z.B. von einem Eventhandler eines anderen Dialogs ausgeführt wird).
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise resolved immer zu true, nachdem die Box geschlossen wurde (egal ob per OK-Button oder Esc-Taste)
 */
export async function warningBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    return new Promise(resolve => {
        let d = showWarningBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId);
        d.addEventListener("close", () => resolve(true));
    });
}

/**
 * Asynchrone Version von showInfoBoxDialog: Diese Funktion kann mit await aufgerufen werden, woraufhin der Aufrufer wartet, bis die Dialogbox
 * wieder geschlossen wurde.
 * @param {String} title Titel für die Überschrift der InfoBox
 * @param {String} contentHTML HTML-Code für den Inhalt der Dialogbox (Absätze, ggf. List-Items etc., ohne Überschrift und Button)
 * @param {String} okLabel Label für den (OK-/Close-)Button, z.B. "OK", "Schließen". Default bei Nicht-Angabe: "OK"
 * @param {String} fallbackContentText Plaintext, der in einer "alert()"-Box angezeigt werden soll, falls !supportsDialog(). Kann beliebig (auch undefined) bleiben, 
 * falls supportsDialog() sichergestellt ist (durch Aufruf, oder  implizit, wenn die Meldung z.B. von einem Eventhandler eines anderen Dialogs ausgeführt wird).
 * @param {String} optionalId <em>Wenn</em> hier ein String übergeben wird, dann wird dieser dem Dialog als ID-Attribut zugewiesen (muss dann natürlich 
 * dokumentweit eindeutig sein), und dann bleibt der Dialog nach dem Schließen im DOM vorhanden. Erneute Folgeaufrufe dieser Methode mit derselben ID
 * führen dann einfach wieder zur Neuanzeige der unveränderten Dialogbox, die anderen Parameter werden dann ignoriert (also nur beim allerersten Aufruf
 * ausgewertet). Wenn der Parameter <em>falsy</em> (z.B. undefined, null oder "") ist, bekommt der Dialog <em>keine</em> ID und <em>wird sofort nach dem 
 * Schließen wieder aus dem DOM entfernt</em>. Das ist insbesondere wichtig, falls bei Folgeaufrufen der Dialogcontent (title, content, ...) sich ändern kann,
 * also eben <em>nicht</em> der vorherige Dialog unverändert wieder angezeigt werden soll, sondern der Dialog jedes Mal neu gerendert werden soll.
 * @returns {Promise} Promise resolved immer zu true, nachdem die Box geschlossen wurde (egal ob per OK-Button oder Esc-Taste)
 */
export async function infoBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId) {
    return new Promise(resolve => {
        let d = showInfoBoxDialog(title, contentHTML, okLabel, fallbackContentText, optionalId);
        d.addEventListener("close", () => resolve(true));
    });
}


/**
 * Hilfsmethode, um einen bereits im Dokument vorhandenen Dialog asynchron per Await auszuführen: Diese Methode registriert dazu temporär einen Close-Eventhandler
 * am Dialog, ruft {@code showModal()} auf und nach Schließen des Dialogs wird (nach Entfernen des Eventhandlers) der resultValue des Dialogs direkt
 * als Funktionsergebnis zurückgegeben (und auf der Dialog-Instanz wieder auf null zurückgesetzt). Bei Cancel per Escape wird "" ausgegeben.
 * Für den Fall, dass der Aufrufer nach dem ShowModal noch Dinge ändern (wie etwas per Script fokussieren) möchte, wird außerdem auf dem Dialog-Element
 * ein "show"-Event getriggert (non-bubbling) mit ev.target==dialog.
 * @param {Node} dialogToShow Dialog-Element (DOM)
 * @returns {Promise} Promise, das zum ReturnValue resolved, also typischerweise dem Value-Attribut des gedrückten Buttons (in einem Form method="dialog")
 * oder zu "" bei Schließen per Escape.
 */
export async function modalDialog(dialogToShow) {
    let _resolve;
    const closeListener = () => {
        dialogToShow.removeEventListener("close", closeListener);
        _resolve(dialogToShow.returnValue);
        dialogToShow.returnValue = null;
    };
    return new Promise(resolve => {
        _resolve = resolve;
        dialogToShow.addEventListener("close", closeListener);
        dialogToShow.returnValue = "";
        dialogToShow.showModal();
        const event = new Event("show", {bubbles: false});
        dialogToShow.dispatchEvent(event);
    });
} 